import type { AllCountryCodes, PlayerClubSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deDivisionsList, frDivisionsList, itDivisionsList, matchAndClubCountryCodes, playerClubSchema } from "@teamz/common";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn, hasClubDivisionDico } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

export function PlayerClubs({ clubs, userId }: { clubs?: RouterOutputs["player"]["getPlayer"]["playerClubs"]; userId: string }) {
  const [modalState, updateModalState] = useState<"open-edit" | "open-create" | "close">("close");
  const [playerClub, updatePlayerClub] = useState<RouterOutputs["player"]["getPlayer"]["playerClubs"][number]>();
  const { t } = useTranslation(["profile", "common"]);
  const { getCountryName } = useTranslationUtils();

  const { user } = useMe();

  return (
    <div className="flex flex-col gap-2">
      <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">{t("profile:current-clubs")}</h3>
      <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
        {clubs?.length
          ? (
              clubs?.map((club) => {
                return (
                  <button
                    type="button"
                    onClick={() => {
                      updateModalState("open-edit");
                      updatePlayerClub(club);
                    }}
                    key={club.id}
                    className={cn(
                      "flex h-[72px] w-full items-center gap-3 rounded-xl bg-neutral-200 p-4",
                      user.role === "club" && !user.isAdmin && "pointer-events-none",
                    )}
                  >
                    <div className="flex flex-1 items-center justify-between gap-2">
                      {/* Fiche servie à une structure freemium : le serveur
                          n'envoie pas le nom du club, seule la division reste
                          exploitable pour le recrutement. */}
                      <span className={cn("line-clamp-3 text-balance text-start font-medium", !club.clubName && "text-neutral-450")}>
                        {club.clubName ?? t("common:freemium.locked-club")}
                      </span>
                      <div className="flex shrink-0 flex-col items-center gap-x-3 gap-y-1 sm:flex-row md:flex-col lg:flex-row">
                        <span className="text-nowrap text-sm font-medium text-neutral-500">{club.clubDivision}</span>
                        <div className="flex shrink-0 items-center gap-2 rounded-full bg-neutral-100 p-0.5">
                          <CircleFlag countryCode={club.clubCountry} className="size-5 rounded-full" />
                          <span className="pr-3 text-sm">
                            {getCountryName(club.clubCountry.toUpperCase() as AllCountryCodes)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {(user.role !== "club" || user.isAdmin) && <EditIcon className="size-5 shrink-0 text-neutral-500" />}
                  </button>
                );
              })
            )
          : (
              user.role === "club" && (
                <button
                  type="button"
                  className="pointer-events-none flex h-[72px] w-full items-center justify-center gap-3 rounded-xl border border-neutral-400 bg-neutral-50 p-4"
                >
                  <DotsIcon />
                </button>
              )
            )}

        { (user.role !== "club" || user.isAdmin) && (
          <Sheet open={modalState === "open-create" || modalState === "open-edit"} onOpenChange={open => updateModalState(open ? "open-create" : "close")}>
            <SheetTrigger asChild>
              <button
                onClick={() => updateModalState("open-create")}
                type="button"
                className="flex h-[72px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-400 bg-neutral-50"
              >
                <AddIcon className="size-6 text-orange-500" />
                <span className="text-sm font-semibold">{t("profile:add-club")}</span>
              </button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                {modalState === "open-create"
                  ? (
                      <SheetTitle>
                        {t("profile:add-club")}
                      </SheetTitle>
                    )
                  : (
                      <SheetTitle>
                        {t("profile:edit-club")}
                      </SheetTitle>
                    )}
              </SheetHeader>

              <UpsertClubForm club={modalState === "open-edit" ? playerClub : undefined} userId={userId} closeModal={() => updateModalState("close")} />

            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}

function UpsertClubForm({ club, userId, closeModal }: { club?: RouterOutputs["player"]["getPlayer"]["playerClubs"][number]; userId: string; closeModal: () => void }) {
  const { getCountryName } = useTranslationUtils();
  const { t } = useTranslation(["common"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.upsertPlayerClub.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const { mutate: mutateDelete, isPending: isPendingDelete } = useMutation(trpc.player.deletePlayerClub.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const playerClubForm = useForm<PlayerClubSchema>({
    resolver: zodResolver(playerClubSchema),
    defaultValues: {
      id: club?.id || undefined,
      clubName: club?.clubName || undefined,
      clubCountry: club?.clubCountry || undefined,
      clubDivision: club?.clubDivision || undefined,
    } as PlayerClubSchema,
  });

  const onSubmit = playerClubForm.handleSubmit((data) => {
    mutate({ userId, playerClub: data });
  });

  const onDelete = () => {
    if (!club?.id)
      return;
    mutateDelete({
      userId,
      id: club.id,
    });
  };

  const countryClub = playerClubForm.watch("clubCountry");

  const clubFormError = playerClubForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerClubForm" onSubmit={onSubmit}>
        <TextField
          label={t("common:club-name")}
          errorMessage={tDyn(t, clubFormError.clubName?.message)}
          {...playerClubForm.register("clubName")}
        />

        <Controller
          control={playerClubForm.control}
          name="clubCountry"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:country")}</Label>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  playerClubForm.setValue("clubDivision", "");
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectGroup>
                    {matchAndClubCountryCodes.map(country => (
                      <SelectItem key={country} value={country}>
                        {getCountryName(country.toUpperCase() as AllCountryCodes)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        {
          (
            hasClubDivisionDico(countryClub))
            ? (
                <TextField
                  label="Division"
                  errorMessage={tDyn(t, clubFormError.clubDivision?.message)}
                  {...playerClubForm.register("clubDivision")}
                />
              )
            : (
                <Controller
                  control={playerClubForm.control}
                  name="clubDivision"

                  render={({ field, fieldState }) => (
                    <RootField>
                      <Label>Division</Label>
                      <Select value={field.value} onValueChange={field.onChange} disabled={!countryClub}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          {(countryClub === "fr" || countryClub === "re") && (
                            <SelectGroup>
                              {frDivisionsList.map(division => (
                                <SelectItem key={division} value={division}>
                                  {division}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}

                          {countryClub === "de" && (
                            <SelectGroup>
                              {deDivisionsList.map(division => (
                                <SelectItem key={division} value={division}>
                                  {division}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}

                          {countryClub === "it" && (
                            <SelectGroup>
                              {itDivisionsList.map(division => (
                                <SelectItem key={division} value={division}>
                                  {division}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>
                      {fieldState.error?.message && (
                        <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
                      )}
                    </RootField>
                  )}
                />
              )
        }
      </SheetForm>

      {
        club
        && (
          <Button
            type="button"
            className="text-white"
            onClick={onDelete}
            variant="ghost"
          >
            {t("common:delete-club")}
          </Button>
        )
      }

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerClubForm" loading={isPendingDelete || isPending || playerClubForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}
